import { Dialog } from '@angular/cdk/dialog';
import {
  Injectable,
  NgZone,
  StaticProvider,
  Type,
  inject,
} from '@angular/core';
import {
  BoDialogSize,
  boDialogOptions,
} from '@3ddv/software-division-components/backoffice/dialog';
import {
  DialogService,
  HlmDialogContent,
} from '@3ddv/software-division-components/generic/dialog';
import { Observable, asyncScheduler, observeOn } from 'rxjs';

export type DialogCloseReason = 'success' | 'cancel';

export interface AppDialogConfig {
  size?: BoDialogSize;
  inputs?: Record<string, unknown>;
  providers?: StaticProvider[];
}

@Injectable({ providedIn: 'root' })
export class AppDialogService {
  private readonly dialogService = inject(DialogService);
  private readonly cdkDialog = inject(Dialog);
  private readonly zone = inject(NgZone);

  open<T, R = DialogCloseReason>(
    component: Type<T>,
    { size = 'md', inputs = {}, providers }: AppDialogConfig = {}
  ) {
    const { reference, outputs } = this.dialogService.open(
      component,
      boDialogOptions(size, {
        context: inputs,
        providers,
        closeOnBackdropClick: false,
      })
    );
    this.keepContentChecked(reference.closed$);

    return {
      closed$: (reference.closed$ as Observable<R | undefined>).pipe(
        observeOn(asyncScheduler)
      ),
      outputs,
    };
  }

  private keepContentChecked(closed$: Observable<unknown>) {
    const content = this.cdkDialog.openDialogs.at(-1)?.componentRef
      ?.instance as HlmDialogContent | undefined;
    const hosted = content?.projectedRef;
    if (!hosted) {
      return;
    }

    const turns = this.zone.onMicrotaskEmpty.subscribe(() => {
      if (!hosted.hostView.destroyed) {
        hosted.changeDetectorRef.detectChanges();
      }
    });
    closed$.subscribe(() => turns.unsubscribe());
  }
}
